import getAPI from "@API/index";
import {
  ApiResponse,
  StopFactorReportResponse,
  StopFactorSettingResponse,
} from "@Dao/responseTypes";
import {
  StopFactorOwner,
  StopFactorReport,
  StopFactorSetting,
} from "@Constants/types";

type OwnerRef = Pick<StopFactorOwner, "type" | "id" | "customerId">;

const ownerUrl = ({ type, id }: OwnerRef) => `/api/StopFactor/${type}/${id}`;

// Даты из БД бэк отдаёт без зоны, хотя хранит UTC — без «Z» браузер сдвинет время на свой пояс.
const parseUtcDate = (value: string | null): Date | null => {
  if (!value) return null;
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
  return new Date(hasZone ? value : `${value}Z`);
};

const mapReport = (response: StopFactorReportResponse): StopFactorReport => ({
  calculatedAt: parseUtcDate(response.calculatedAt),
  triggeredCount: response.triggeredCount,
  items: response.items.map((item) => ({
    key: item.key,
    displayName: item.displayName,
    status: item.status,
    value: item.value,
  })),
});

const mapSettings = (response: StopFactorSettingResponse[]): StopFactorSetting[] =>
  response.map((setting) => ({
    key: setting.key,
    displayName: setting.displayName,
    isEnabled: setting.isEnabled,
  }));

export async function getStopFactorReport(owner: OwnerRef): Promise<StopFactorReport> {
  const response = await getAPI().request<ApiResponse<StopFactorReportResponse>>({
    method: "get",
    url: ownerUrl(owner),
    params: { customerId: owner.customerId },
    suppressLoading: true,
  });
  return mapReport(response.data);
}

export async function recalculateStopFactors(owner: OwnerRef): Promise<StopFactorReport> {
  const response = await getAPI().request<ApiResponse<StopFactorReportResponse>>({
    method: "post",
    url: `${ownerUrl(owner)}/recalculate`,
    params: { customerId: owner.customerId },
    suppressLoading: true,
    successText: "StopFactors.recalculated",
  });
  return mapReport(response.data);
}

export async function getStopFactorSettings(customerId: string): Promise<StopFactorSetting[]> {
  const response = await getAPI().request<ApiResponse<StopFactorSettingResponse[]>>({
    method: "get",
    url: `/api/StopFactor/settings/${customerId}`,
  });
  return mapSettings(response.data);
}

export async function updateStopFactorSettings(
  customerId: string,
  enabledKeys: string[]
): Promise<StopFactorSetting[]> {
  const response = await getAPI().request<ApiResponse<StopFactorSettingResponse[]>>({
    method: "put",
    url: `/api/StopFactor/settings/${customerId}`,
    data: { enabledKeys },
    successText: "StopFactors.settingsSaved",
  });
  return mapSettings(response.data);
}
